import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormCheck, Modal } from 'react-bootstrap';
import {
  BsBatteryHalf,
  BsFileEarmarkText,
  BsPencilSquare,
  BsFlagFill,
} from 'react-icons/bs';

import { ConsumerUnit } from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';

import consumerUnitImg from 'assets/images/consumer_unit.jpeg';

import { format } from 'utils/format';
import { getInvoiceAlert } from 'utils/invoiceAlert';
import { getUtilityCompanyLogo } from 'utils/logo';
import { consumption } from 'utils/helpers';
import { periodConsumptionKwh } from 'utils/invoiceConsumption';
import {
  HIGH_BALANCE_MONTHS,
  unitCreditBalance,
} from 'utils/managerIndicators';

import { InvoiceAlertPill } from 'components/invoice-alert-pill/invoice-alert-pill.component';
import { InvoiceDownloadButton } from 'components/invoice-download-button/invoice-download-button.component';

import { FavouriteStar } from 'components/favourite-star/favourite-star.component';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import {
  selectConsumerUnitInvoices,
  selectSelectedConsumerUnitIds,
} from 'store/consumer_unit/consumer_unit.selector';
import { getConsumerUnitInvoices } from 'store/consumer_unit/consumer_unit.action';
import {
  addSelectedConsumerUnitId,
  removeSelectedConsumerUnitId,
} from 'store/consumer_unit/consumer_unit.slice';

import { setModalInvoiceState } from 'store/modal_invoice/modal_invoice.slice';
import { selectCurrentFlagList } from 'store/current_flag/current_flag.selector';
import { getRefMonthFlag } from 'utils/flag';
import { EFlagColor } from 'utils/constants';

import {
  CardDataContainer,
  SelectConsumerUnit,
  CardImage,
  FavouriteSlot,
  InfoData,
  InfoAddress,
  ConsumerUnitCardContainer,
  OptionsContainer,
  Option,
  InfoTitle,
  Company,
  CompanyImg,
  Icon,
  ModalHeader,
  ModalHeaderSection,
  ModalHeaderTitle,
  ModalContent,
  ModalTable,
  Wrapper,
  LabelContainer,
  Label,
  Select,
  Pagination,
  PaginationButton,
  InfoDataWarning,
  Metrics,
  Metric,
  MetricLabel,
  MetricValue,
  CreditHighTag,
  Status,
  UploadInvoiceButton,
} from './consumer-unit-card.styles';
import { AuditPill } from 'components/audit-pill/audit-pill.component';

interface ConsumerUnitCardProps {
  consumerUnit: ConsumerUnit;
  invoiceList: Invoice[];
  monthQuantity: number;
  refMonthList: string[];
  /** Arrastar para reordenar o carrossel (out/2026). */
  dragProps?: React.HTMLAttributes<HTMLDivElement> & { draggable?: boolean };
  isDragOver?: boolean;
}

export const ConsumerUnitCard = ({
  consumerUnit,
  invoiceList: allInvoices,
  monthQuantity,
  refMonthList,
  dragProps,
  isDragOver,
}: ConsumerUnitCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [show, setShow] = useState(false);
  const [newInvoiceList, setNewInvoiceList] = useState<Invoice[]>([]);

  const [filter, setFilter] = useState<string>('all');

  const handleClose = () => setShow(false);
  const handleShow = () => {
    dispatch(getConsumerUnitInvoices({ consumerUnitId: consumerUnit.id }));
    setShow(true);
  };

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const invoiceList = useAppSelector(selectConsumerUnitInvoices);
  const selectedConsumerUnitIds = useAppSelector(selectSelectedConsumerUnitIds);
  const currentFlagList = useAppSelector(selectCurrentFlagList);

  // Aviso de fatura em aberto (hasDebt) / a vencer (D-3) — mesmas regras do
  // backend (@shared/utils/invoiceAlerts), sem chamada nova à API.
  const invoiceAlert = getInvoiceAlert(consumerUnit.invoiceList);

  // Saldo de créditos da fatura mais recente (todas as faturas, não só as do
  // período) — out/2026, pedido do Bruno. Aparece com saldo zero quando a UC
  // participa da compensação; "alto" acima de 3 meses de consumo.
  const credit = unitCreditBalance(consumerUnit, allInvoices);

  useEffect(() => {
    if (invoiceList.length) {
      const newInvoiceList = refMonthList.map((x) => {
        const invoice =
          invoiceList.find((invoice) => invoice.refMonth === x) ||
          ({ id: x, refMonth: x, invoiceValue: -1 } as Invoice);
        return invoice;
      });

      setNewInvoiceList(newInvoiceList);
    }
  }, [invoiceList]);

  useEffect(() => {
    if (selectedConsumerUnitIds.includes(consumerUnit.id)) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [selectedConsumerUnitIds]);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      dispatch(addSelectedConsumerUnitId({ id: consumerUnit.id }));
    } else {
      dispatch(removeSelectedConsumerUnitId({ id: consumerUnit.id }));
    }
  };

  const getTotalValue = () =>
    consumerUnit.invoiceList.reduce((acc, item) => acc + item.invoiceValue, 0);

  // Mesma regra do Resumo (kWh faturado por posto) — ver periodConsumptionKwh
  const getTotalConsumption = () =>
    consumerUnit.invoiceList.reduce(
      (acc, item) => acc + periodConsumptionKwh(item),
      0
    );

  const handleUploadFile = () => {
    handleClose();
    dispatch(setModalInvoiceState({ modalState: 'select_invoice' }));
  };

  return (
    <ConsumerUnitCardContainer
      className={`d-inline-block uc-card${isDragOver ? ' drag-over' : ''}`}
      active={isSelected}
      {...dragProps}
      // disabled={isDisabled}
    >
      <SelectConsumerUnit className="px-3 py-2">
        <span className="me-2">selecionar</span>
        <FormCheck
          type="checkbox"
          name="hasMarketing"
          onChange={handleSelect}
          checked={isSelected}
        />
      </SelectConsumerUnit>

      <CardImage
        style={{
          backgroundImage: `url(${consumerUnit.imageUrl || consumerUnitImg})`,
        }}
      >
        {/* Favorito (Figma, out/2026) — favoritos abrem o carrossel */}
        <FavouriteSlot>
          <FavouriteStar
            consumerUnitId={consumerUnit.id}
            isFavourite={consumerUnit.isFavourite}
            onPhoto
          />
        </FavouriteSlot>
      </CardImage>
      <CardDataContainer className="ms-4 ps-2 pb-4">
        <InfoTitle
          className="mb-2 mt-4"
          title={consumerUnit.name || consumerUnit.installation}
        >
          {consumerUnit.name || consumerUnit.installation}
        </InfoTitle>
        <InfoAddress className="mb-2">
          {/* <BsFillGeoAltFill size={13} /> */}
          <Icon
            src="/geo-alt-fill.svg"
            className="me-2"
            alt="Localização"
          ></Icon>
          {`${consumerUnit?.city?.name}/${consumerUnit?.city?.state?.code}`}
        </InfoAddress>
        <InfoData>
          R$ {format.money(getTotalValue())}
          <Icon src="/dot.svg" className="ms-2 me-2" alt=""></Icon>
        </InfoData>
        <InfoDataWarning
          hasError={monthQuantity > consumerUnit.invoiceList.length}
        >
          {consumerUnit.invoiceList.length}/{monthQuantity} fatura(s)
        </InfoDataWarning>
        {invoiceAlert && <InvoiceAlertPill alert={invoiceAlert} />}
        {/* Consumo e créditos separados em mini-métricas com ícone (26/out/2026,
            opção B do Bruno): o raio é o ícone de consumo do Figma; o detalhe
            fica no tooltip para o card continuar enxuto. */}
        <Metrics>
          <Metric title="Energia fornecida pela distribuidora no período selecionado">
            <MetricLabel>
              <Icon src="/lightning-fill.svg" alt=""></Icon>
              consumo
            </MetricLabel>
            <MetricValue>
              {format.energy(getTotalConsumption())} kWh
            </MetricValue>
          </Metric>
          {credit && (
            <Metric
              title={
                credit.balance > 0 && Number.isFinite(credit.monthsCovered)
                  ? `Saldo de créditos na fatura mais recente — cobre cerca de ${format.number(
                      credit.monthsCovered,
                      1
                    )} meses do consumo médio`
                  : 'Saldo de créditos na fatura mais recente'
              }
            >
              <MetricLabel>
                <BsBatteryHalf size={14} aria-hidden="true" />
                créditos
                {credit.balance > 0 &&
                  credit.monthsCovered > HIGH_BALANCE_MONTHS && (
                    <CreditHighTag>alto</CreditHighTag>
                  )}
              </MetricLabel>
              <MetricValue>{format.energy(credit.balance)} kWh</MetricValue>
            </Metric>
          )}
        </Metrics>
        {/* disabled={isDisabled} */}
        <Company>
          <CompanyImg
            src={getUtilityCompanyLogo(consumerUnit.utilityCompany.slug)}
            alt={consumerUnit.utilityCompany.name}
          />
        </Company>
      </CardDataContainer>
      <OptionsContainer>
        <Option
          type="button"
          onClick={handleShow}
          title="Listar as faturas"
          aria-label="Listar as faturas"
        >
          <BsFileEarmarkText size={16} />
        </Option>
        <Option
          type="button"
          onClick={() => navigate(`/unidades-consumidoras/${consumerUnit?.id}`)}
          title="Abrir o imóvel"
          aria-label="Abrir o imóvel"
        >
          <BsPencilSquare size={16} />
        </Option>
      </OptionsContainer>
      <Modal show={show} onHide={handleClose} size="xl">
        <Modal.Body className="p-0">
          <ModalHeader className={`px-4 py-3 d-flex justify-content-between`}>
            <div>
              <ModalHeaderSection className={`mb-0`}>
                Minhas faturas
              </ModalHeaderSection>
              <ModalHeaderTitle className="mb-0">
                {consumerUnit?.name}
              </ModalHeaderTitle>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="35"
              height="35"
              viewBox="0 0 35 35"
              fill="none"
              onClick={handleClose}
            >
              <path
                d="M25.9853 8.55022C26.5711 9.13601 26.5711 10.0858 25.9853 10.6715L19.6213 17.0355L25.9853 23.3995C26.5711 23.9853 26.5711 24.935 25.9853 25.5208C25.3995 26.1066 24.4497 26.1066 23.864 25.5208L17.5 19.1568L11.136 25.5208C10.5502 26.1066 9.6005 26.1066 9.01472 25.5208C8.42893 24.935 8.42893 23.9853 9.01472 23.3995L15.3787 17.0355L9.01472 10.6715C8.42893 10.0858 8.42893 9.13601 9.01472 8.55022C9.6005 7.96444 10.5503 7.96444 11.136 8.55022L17.5 14.9142L23.864 8.55022C24.4497 7.96444 25.3995 7.96444 25.9853 8.55022Z"
                fill="white"
              />
            </svg>
          </ModalHeader>
          <ModalContent className="px-4 py-5 d-flex flex-column">
            <Wrapper className="pb-4 mb-2">
              <LabelContainer>
                <Label className="mb-2">Situação</Label>
                <Select
                  className="ps-2"
                  name="initialFilter"
                  onChange={(e) => setFilter(e.target.value)}
                  value={filter}
                >
                  <option value="all">Todas</option>
                  <option value="pending">Pendentes</option>
                  <option value="completed">Completas</option>
                </Select>
              </LabelContainer>
            </Wrapper>
            <ModalTable>
              <thead>
                <th>Status</th>
                <th>Mês de referência</th>
                <th>Bandeira</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Consumo (kWh)</th>
                <th>Geração (kWh)</th>
                <th>Auditoria</th>
                <th>&nbsp;</th>
                <th>&nbsp;</th>
              </thead>
              <tbody>
                {newInvoiceList
                  .filter((invoice) => {
                    if (filter === 'pending' && invoice.invoiceValue < 0) {
                      return invoice;
                    }
                    if (filter === 'completed' && invoice.invoiceValue >= 0) {
                      return invoice;
                    }
                    if (filter === 'all') return invoice;
                  })
                  .map((invoice) => (
                    <tr key={invoice.id}>
                      <td>
                        <Status isPending={invoice.invoiceValue < 0}>
                          {invoice.invoiceValue >= 0 ? 'Ok' : 'Pendente'}
                        </Status>
                      </td>
                      <td>{format.refMonth(invoice.refMonth)}</td>
                      <td>
                        <BsFlagFill
                          size={16}
                          color={
                            EFlagColor[
                              getRefMonthFlag(invoice.refMonth, currentFlagList)
                                ?.flag.slug as keyof typeof EFlagColor
                            ]
                          }
                        />
                      </td>
                      <td>
                        {invoice.invoiceValue >= 0
                          ? format.date(invoice.dueDate)
                          : '-'}
                      </td>
                      <td>
                        {invoice.invoiceValue >= 0
                          ? `R$ ${format.money(invoice.invoiceValue)}`
                          : '-'}
                      </td>
                      <td>
                        {invoice.invoiceValue >= 0
                          ? format.energy(
                              consumption(
                                invoice.suppliedEnergy.currentRead,
                                invoice.suppliedEnergy.previousRead,
                                invoice.suppliedEnergy.factorRead
                              )
                            )
                          : '-'}
                      </td>
                      <td>
                        {consumerUnit.hasFv && invoice.invoiceValue >= 0
                          ? format.energy(
                              consumerUnit.monthGenerationList.find(
                                (x) => x.refMonth === invoice.refMonth
                              )?.value || 0
                            )
                          : '-'}
                      </td>
                      <td>
                        <AuditPill invoice={invoice} />
                      </td>
                      <td>
                        {invoice.invoiceValue >= 0 ? (
                          <a
                            href={`${process.env.REACT_APP_URL}/relatorio/${invoice.id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <span>ver relatório</span>{' '}
                            <img src="/box-arrow-up-right.svg" alt=""></img>
                          </a>
                        ) : (
                          <UploadInvoiceButton onClick={handleUploadFile}>
                            Importar fatura
                          </UploadInvoiceButton>
                        )}
                      </td>
                      <td>
                        {invoice.invoiceValue >= 0 && (
                          <InvoiceDownloadButton invoiceId={invoice.id} />
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </ModalTable>
            {/* <Pagination className="pt-4 mt-2">
              <PaginationButton>Anterior</PaginationButton>
              <PaginationButton className="active">1</PaginationButton>
              <PaginationButton>2</PaginationButton>
              <PaginationButton>3</PaginationButton>
              <PaginationButton>Próxima</PaginationButton>
            </Pagination> */}
          </ModalContent>
        </Modal.Body>
      </Modal>
    </ConsumerUnitCardContainer>
  );
};
